import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

/**
 * Tracks the tenant the user has drilled into, so the TopBar can show
 * "10NEWTONS — Tenant Name" in the title.
 *
 * Used by:
 *   - Superadmin's TenantDetail page (sets it after the tenant loads,
 *     clears it on unmount).
 *   - Anything else that wants to scope the title bar to a tenant.
 *
 * For tenant_admin and participant users the title comes from their
 * membership instead, so they don't need to call setName() — the
 * TopBar reads tenant_name straight off the active membership.
 */
interface SelectedTenantContextValue {
  name: string | null;
  setName(name: string | null): void;
}

const SelectedTenantContext = createContext<SelectedTenantContextValue | null>(null);

export function SelectedTenantProvider({ children }: { children: ReactNode }) {
  const [name, _setName] = useState<string | null>(null);
  // Memoise so consumers calling setName in an effect don't re-trigger
  // the effect on every parent re-render.
  const setName = useCallback((next: string | null) => _setName(next), []);
  return (
    <SelectedTenantContext.Provider value={{ name, setName }}>
      {children}
    </SelectedTenantContext.Provider>
  );
}

export function useSelectedTenant(): SelectedTenantContextValue {
  const ctx = useContext(SelectedTenantContext);
  if (!ctx) {
    // Outside the provider (e.g. unit tests that don't wrap) — return
    // a no-op so consumers don't have to guard.
    return { name: null, setName: () => {} };
  }
  return ctx;
}
